/**
 * Single source of truth for which paths the marketing app owns.
 *
 * The client router (`public-marketing-app.tsx`), the prerender build
 * (`scripts/prerender-marketing.ts`), and the edge worker (`worker/index.ts`)
 * all consume this module so the "known route" set cannot drift between them.
 *
 * A7Y-2104: the worker uses {@link isKnownMarketingRoute} to return a real
 * HTTP 404 for unmatched paths instead of serving the SPA shell at 200
 * (`not_found_handling: single-page-application` makes every unmatched path
 * resolve to `index.html` at 200 unless the worker intervenes). Deriving the
 * 404 decision from this shared registry — rather than a hand-maintained second
 * list — is what keeps a valid page from ever being 404'd.
 */
import { SEO_METADATA } from './seo-metadata';
import { isSecurityPublicPath } from './security-launch-control';

/**
 * Marketing paths served by the client SPA but NOT prerendered with per-route
 * SEO meta (Clerk sign-up/in widgets, redirects, the affiliate portal). The
 * prerender build still emits a shell `index.html` for each of these, so a hard
 * refresh resolves to a real 200 asset rather than the not-found fallback.
 *
 * This is the single source of truth: `public-marketing-app.tsx` and
 * `scripts/prerender-marketing.ts` both import it.
 */
export const PUBLIC_CLIENT_ONLY_MARKETING_PATHS = [
  '/ttest',
  '/check-my-app',
  '/schedule-demo',
  '/signup',
  '/sign-in',
  '/unsubscribe',
  '/email-preferences',
  '/email-cadence',
  // A7Y-6115: Weekly Bulletin double opt-in landing pages (noindex).
  '/newsletter/confirm',
  '/newsletter/confirmed',
  // A7Y-1897: affiliate portal (dark, direct-URL only — no footer link).
  '/affiliates/signup',
  '/affiliates/confirm',
  '/affiliates/login',
  '/affiliates/redeem',
  '/affiliates/dashboard',
] as const;

/**
 * App-owned route families served from the preserved full app shell
 * (`app-shell.html`) rather than a marketing page: the Clerk-gated app,
 * `/logout`, and the public scan funnel (including `/scan/r/<auditId>` share
 * pages). The worker rewrites these to `/app-shell` before hitting ASSETS, so
 * they must be recognised as known routes.
 */
export const APP_SHELL_PATH_RE = /^(?:\/app(?:\/|$)|\/logout\/?$|\/scan(?:\/|$))/;

/**
 * Dynamic marketing route with a path param that is NOT prerendered as a static
 * file and is not part of the app shell: the quick-audit result share page
 * (`/check-my-app/r/<token>`). It is served via the SPA fallback, so it must be
 * recognised as a known route rather than 404'd.
 */
const CHECK_MY_APP_RESULT_RE = /^\/check-my-app\/r\/[^/]+$/;

/**
 * Merged marketing pages (A7Y-5446; spec 02-aso-on-rails-start-page-spec.md §7):
 * merged path → the page that now carries its content. The worker answers every
 * key with a 301 to its target (query string preserved, see
 * {@link mergedMarketingRedirect}); no other surface — `SEO_METADATA`, the
 * sitemap, `llms.txt`, the route tables, the header, the footer, the start menu
 * or an in-page link — carries a key. `/diy` became a blog article
 * (A7Y-5455); `/appcheck` joins when `/start` replaces the root (A7Y-5449).
 */
export const MERGED_MARKETING_ROUTES: Record<string, string> = {
  '/features': '/methodology',
  '/resources/best-practices-baked-in': '/methodology',
  '/resources/scaling-aso-with-ai': '/methodology',
  '/continuous-monitoring': '/how-it-works',
  '/features/daily-audit': '/how-it-works',
  '/resources/aso-workflow': '/how-it-works',
  '/ai-discovery': '/engines/ai-discoverability',
  '/global': '/engines/localization-analysis',
  '/features/app-portfolios': '/pricing',
  '/contact-sales': '/contact?intent=sales',
  '/faq': '/pricing',
  '/diy': '/blog/posts/how-to-build-your-own-apptonomy/',
};

/**
 * The absolute 301 location for a merged path, or `null` when `url` is not one.
 * The target's own query parameters come first; the request's are appended, so
 * `/contact-sales?utm_source=x` → `/contact?intent=sales&utm_source=x`.
 */
export function mergedMarketingRedirect(url: URL): string | null {
  const path = normalizePath(url.pathname);
  if (!Object.prototype.hasOwnProperty.call(MERGED_MARKETING_ROUTES, path)) return null;
  const target = MERGED_MARKETING_ROUTES[path];
  const location = new URL(target, url.origin);
  for (const [key, value] of url.searchParams) location.searchParams.append(key, value);
  return location.toString();
}

/** Prerendered marketing pages — exactly the keys of the shared SEO registry. */
export const PRERENDERED_MARKETING_PATHS = new Set<string>(Object.keys(SEO_METADATA));
const CLIENT_ONLY_MARKETING_PATHS = new Set<string>(PUBLIC_CLIENT_ONLY_MARKETING_PATHS);

/** Drop a single trailing slash (except on the root) so `/pricing/` === `/pricing`. */
function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

/**
 * True when `pathname` is a route this app owns: a prerendered marketing page
 * (from `SEO_METADATA`), a client-only marketing page, the quick-audit result
 * share page, or an app-shell family (`/app`, `/logout`, `/scan`). Everything
 * else is an unmatched path that the worker returns a real HTTP 404 for
 * instead of the SPA shell.
 *
 * Worker early-return routes (`/sitemap.xml`, `/sitemap-marketing.xml`,
 * `/llms.txt`, `/.well-known/security.txt`, `/security/policy`) and the
 * `/vnext`, legacy `/security-policy` / `/security-advisories` and merged-page
 * (`MERGED_MARKETING_ROUTES`) redirects are handled before the catch-all and
 * never reach this check; a merged path still reads as known.
 */
export function isKnownMarketingRoute(pathname: string): boolean {
  const path = normalizePath(pathname);
  // The worker checks this route family against the host launch gate before
  // assets are fetched. Keeping it recognizable here lets a staging-host test
  // exercise the same source even when Vitest itself has no Vite env value.
  if (isSecurityPublicPath(path)) return true;
  if (Object.prototype.hasOwnProperty.call(MERGED_MARKETING_ROUTES, path)) return true;
  if (PRERENDERED_MARKETING_PATHS.has(path)) return true;
  if (CLIENT_ONLY_MARKETING_PATHS.has(path)) return true;
  if (CHECK_MY_APP_RESULT_RE.test(path)) return true;
  if (APP_SHELL_PATH_RE.test(path)) return true;
  return false;
}
